import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowRightIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import { Tooltip } from '../ui/Tooltip';
import { ServiceFailureBadge } from '../ServiceFailureBadge';
import { abbreviateLocationName, formatMinutes } from './utils';
import { MoveRisk, MoveGroup, ProjectedStatus } from './types';
import styles from './MoveCard.module.css';
import { RouteEta } from './RouteEta';
import type { RouteCascadeEstimate } from './routeEtaCascade';

export interface MoveCardProps {
  move: MoveRisk;
  routeCascade?: RouteCascadeEstimate;
  priority: number;
  visualPriority?: number;
  isCurrentMove: boolean;
  isGroupedDisplay?: boolean;
  group?: MoveGroup;
  projectedStatus?: ProjectedStatus;
  projectedETA?: string;
  projectedETAToDest?: string;
  isETAToDestination?: boolean;
  isLinkedMove?: boolean;
  isDueTomorrow?: boolean;
  linkedArrivalTime?: string;
  /** Layout orientation - vertical mode makes card full width */
  orientation?: 'horizontal' | 'vertical';
  /** Whether to show container number on card */
  showContainerNumber?: boolean;
  /** Whether to show size on card */
  showSize?: boolean;
  /** Whether to show chassis number on card */
  showChassis?: boolean;
  /** Whether to show account status badge (Key Account, Contract, New Location) */
  showAccountStatus?: boolean;
  /** PRO the user came from (e.g. the Pro drawer's PRO) — its moves get a highlighted PRO link */
  highlightPronumber?: number;
  onProClick?: (pronumber: number, legnum: number) => void;
  /** When provided, renders an open-in-drawer icon next to the PRO link */
  onProDetailsClick?: (pronumber: number) => void;
  onRemove?: (move: MoveRisk, group?: MoveGroup) => void;
  onQuickAssign?: (move: MoveRisk, targetUnitnumber: string) => void;
  onDragStart?: (e: React.DragEvent, move: MoveRisk, sourceDriver: string, group?: MoveGroup) => void;
  onDragEnd?: (e: React.DragEvent) => void;
}

function getStatusTitleClass(status: string): string {
  switch (status) {
    case 'Dispatched': return styles.cardTitleNotStarted;
    case 'Completed': return styles.cardTitleCompleted;
    default: return styles.cardTitleInProgress;
  }
}

function getStatusCardClass(status: string): string {
  switch (status) {
    case 'Dispatched': return styles.moveCardNotStarted;
    case 'Completed': return styles.moveCardStatusCompleted;
    default: return styles.moveCardInProgress;
  }
}

export const MoveCard: React.FC<MoveCardProps> = ({
  move, routeCascade, priority, visualPriority, isCurrentMove, isGroupedDisplay = false, group,
  projectedStatus, projectedETA, projectedETAToDest, isETAToDestination, isLinkedMove, isDueTomorrow,
  linkedArrivalTime, orientation = 'horizontal', showContainerNumber, showSize, showChassis, showAccountStatus = true, highlightPronumber, onProClick, onProDetailsClick, onRemove, onQuickAssign, onDragStart, onDragEnd,
}) => {
  const anyGroupMoveStarted = group?.isGroup && group.moves.some(m => m.status !== 'Dispatched');
  const canRemove = move.can_reassign && !anyGroupMoveStarted && !!onRemove;

  const isLocked = !move.can_reassign;
  const isCompleted = move.status === 'Completed';
  const isNotStarted = move.status === 'Dispatched';
  const isNotAccepted = move.status === 'Dispatched' && move.accept_reject !== 'A';

  const isDateToday = (dateStr: string | null | undefined): boolean => {
    if (!dateStr) return false;
    const today = new Date();
    const [month, day] = dateStr.split('/').map(Number);
    return today.getMonth() + 1 === month && today.getDate() === day;
  };

  // For exports, show the earlier of Cutoff or Per Diem
  const getExportDeadlineSubDate = (): { label: string; date: string; time?: string } | null => {
    const isExport = move.movetype !== 'IMPORT';
    const pd = move.legtype === 'PICKUP' ? null : move.per_diem_date;
    const co = isExport ? move.cutoff_date : null;
    const coTime = isExport ? (move.cutoff_time ?? undefined) : undefined;

    if (!pd && !co) return null;
    if (!co) return pd ? { label: 'Per Diem', date: pd } : null;
    if (!pd) return isExport ? { label: 'Cutoff', date: co, time: coTime } : null;

    // Both exist - compare dates (M/D format, assume current year)
    const year = new Date().getFullYear();
    const [pdM, pdD] = pd.split('/').map(Number);
    const [coM, coD] = co.split('/').map(Number);
    const pdDate = new Date(year, pdM - 1, pdD);
    const coDate = new Date(year, coM - 1, coD);

    return coDate <= pdDate ? { label: 'Cutoff', date: co, time: coTime } : { label: 'Per Diem', date: pd };
  };

  const exportSubDate = getExportDeadlineSubDate();

  const [showQuickAssign, setShowQuickAssign] = useState(false);
  const [quickAssignInput, setQuickAssignInput] = useState('');
  const quickAssignRef = useRef<HTMLDivElement>(null);
  const quickAssignInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (quickAssignRef.current && !quickAssignRef.current.contains(event.target as Node)) {
        setShowQuickAssign(false);
        setQuickAssignInput('');
      }
    };
    if (showQuickAssign) {
      document.addEventListener('mousedown', handleClickOutside);
      setTimeout(() => quickAssignInputRef.current?.focus(), 0);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showQuickAssign]);

  const handleQuickAssignSubmit = () => {
    if (quickAssignInput.trim() && onQuickAssign) {
      onQuickAssign(move, quickAssignInput.trim());
      setShowQuickAssign(false);
      setQuickAssignInput('');
    }
  };

  const isDraggable = !isLocked && isNotStarted && !!onDragStart;

  const isApptMissed = move.appt_missed === true;

  // Any delivery reschedule (service failure) on the pro gets a red card border.
  const isRescheduled = (move.reschedule_count ?? 0) > 0;

  // Dimmed when it doesn't pass the active dispatcher/region filter. Dimming
  // lives in CSS (not an inline style) so the .filteredOut:hover rule can
  // temporarily restore the card to full opacity for clicking the Pro link.
  const isFilteredOut = move.matches_dispatcher_filter === false || move.matches_region_filter === false;

  // On missed-appt cards, prepend the deadline date to "Appt: ..." so dispatchers
  // see at a glance which day's appointment was missed (e.g. "Appt: 5/21 16:00").
  const displayedDeadlineTime = (isApptMissed && move.deadline_date && move.deadline_time?.startsWith('Appt:'))
    ? move.deadline_time.replace(/^Appt:\s*/, `Appt: ${move.deadline_date} `)
    : move.deadline_time;

  return (
    <div
      className={`${styles.moveCard} ${getStatusCardClass(move.status)} ${isLocked ? styles.moveCardLocked : ''} ${isCurrentMove ? styles.moveCardCurrent : ''} ${isNotAccepted ? styles.moveCardNotAccepted : ''} ${isApptMissed ? styles.moveCardMissedAppt : ''} ${isRescheduled ? styles.moveCardRescheduled : ''} ${orientation === 'vertical' ? styles.moveCardVertical : ''} ${isFilteredOut ? styles.filteredOut : ''}`}
      draggable={isDraggable}
      onDragStart={(e) => isDraggable && onDragStart!(e, move, move.driver, group)}
      onDragEnd={onDragEnd}
    >
      {/* Priority badge */}
      <div className={styles.priorityBadge}>
        {move.dispatch_priority ?? 0}
      </div>

      {/* Title Bar with Status */}
      <div className={`${styles.cardTitleBar} ${isApptMissed ? styles.cardTitleMissedAppt : getStatusTitleClass(move.status)} ${isNotAccepted && !isApptMissed ? styles.cardTitleNotAccepted : ''}`}>
        <span className={styles.cardTitleStatus}>
          {move.status === 'Dispatched' ? (isNotAccepted ? 'Not Accepted' : 'Not Started') : move.status}
          {(move.status === 'At Origin' || move.status === 'At Destination') && move.sitting_minutes != null && move.sitting_minutes > 0 && (
            <span className={styles.sittingTime}> ({move.sitting_minutes >= 60 ? `${Math.floor(move.sitting_minutes / 60)}h ${move.sitting_minutes % 60}m` : `${move.sitting_minutes}m`})</span>
          )}
          {(isApptMissed || isNotAccepted) && move.dispatch_sent_at && (
            <span className={styles.sittingTime}> - Sent {move.dispatch_sent_at}</span>
          )}
          {/* Service-failure count — same badge/tooltip as the At Risk Monitor's SF column */}
          {isRescheduled && (
            <span className={styles.serviceFailureBadge}>
              <ServiceFailureBadge
                history={move.reschedule_history || []}
                count={move.reschedule_count || 0}
                placement="bottom"
              />
            </span>
          )}
        </span>
        <div className={styles.titleBarActions}>
          {(() => {
            const tickets = move.trouble_tickets || [];
            const openTickets = tickets.filter(t => t.status.toUpperCase() === 'NEW' || t.status.toUpperCase() === 'PENDING');
            if (openTickets.length === 0) return null;
            return (
              <Tooltip
                content={
                  <div className="text-xs space-y-2 max-w-xs">
                    <div className="font-semibold border-b border-gray-300 pb-1 mb-1 text-gray-900">
                      Trouble Tickets ({move.trouble_ticket_count})
                    </div>
                    {tickets.map((ticket) => (
                      <div key={ticket.recno} className="space-y-0.5">
                        <div className="flex items-center gap-1.5">
                          <span className="text-gray-900 font-medium">{ticket.category || 'No Category'}</span>
                          <span className={`inline-flex px-1 py-0.5 rounded text-[0.625rem] font-medium ${
                            ticket.status.toUpperCase() === 'NEW' || ticket.status.toUpperCase() === 'PENDING'
                              ? 'bg-danger text-danger-fg'
                              : ticket.status.toUpperCase() === 'RESOLVED'
                                ? 'bg-ok text-ok-fg'
                                : 'bg-gray-100 text-gray-800'
                          }`}>
                            {ticket.status}
                          </span>
                        </div>
                        <div className="text-gray-400 text-[0.625rem]">{ticket.datetime}</div>
                      </div>
                    ))}
                  </div>
                }
                placement="bottom"
              >
                <span className="inline-flex items-center justify-center w-4 h-4 text-[0.5rem] font-bold text-white bg-red-500 rounded-full cursor-help flex-shrink-0">
                  {openTickets.length}
                </span>
              </Tooltip>
            );
          })()}
          <Tooltip
            content={
              <div className={styles.zoomedContent}>
                <div className={styles.zoomedRow}>
                  <span className={styles.zoomedLabel}>Status:</span>
                  <span className={styles.zoomedValue}>
                    {move.status === 'Dispatched' ? (isNotAccepted ? 'Not Accepted' : 'Not Started') : move.status}
                    {(move.status === 'At Origin' || move.status === 'At Destination') && move.sitting_minutes != null && move.sitting_minutes > 0 && (
                      <> ({move.sitting_minutes >= 60 ? `${Math.floor(move.sitting_minutes / 60)}h ${move.sitting_minutes % 60}m` : `${move.sitting_minutes}m`})</>
                    )}
                  </span>
                </div>
                {!isCompleted && projectedETA && (
                  <div className={styles.zoomedRow}>
                    <span className={styles.zoomedLabel}>ETA:</span>
                    <span className={styles.zoomedValue}>{projectedETA} {isETAToDestination ? 'to Dest' : 'to Orig'}</span>
                  </div>
                )}
                {!isCompleted && <RouteEta moveStatus={move.status} data={move.route_eta} cascade={routeCascade} />}
                {isCompleted && move.dest_arrival_time && (
                  <div className={styles.zoomedRow}>
                    <span className={styles.zoomedLabel}>Arrived:</span>
                    <span className={styles.zoomedValue}>{move.dest_arrival_time}</span>
                  </div>
                )}
                {move.deadline_time && (
                  <div className={styles.zoomedRow}>
                    <span className={styles.zoomedLabel}>Appt:</span>
                    <span className={styles.zoomedValue}>{move.deadline_time}</span>
                  </div>
                )}
                {exportSubDate && (
                  <div className={styles.zoomedRow}>
                    <span className={styles.zoomedLabel}>{exportSubDate.label}:</span>
                    <span className={styles.zoomedValue}>{exportSubDate.date}{exportSubDate.time ? ` ${exportSubDate.time}` : ''}</span>
                  </div>
                )}
                {move.status === 'In Transit' && move.distance_to_dest_miles != null && (
                  <div className={styles.zoomedRow}>
                    <span className={styles.zoomedLabel}>Distance:</span>
                    <span className={styles.zoomedValue}>{move.distance_to_dest_miles} mi remaining{move.driver_speed_mph ? ` at ${move.driver_speed_mph} mph` : ''}</span>
                  </div>
                )}
              </div>
            }
            placement="left"
          >
            <button
              className={styles.zoomButton}
              onClick={(e) => e.stopPropagation()}
              title="View larger"
            >
              <MagnifyingGlassIcon className="w-3.5 h-3.5" />
            </button>
          </Tooltip>
          {canRemove && (
            <button
              className={styles.removeButton}
              onClick={(e) => {
                e.stopPropagation();
                onRemove!(move, group);
              }}
              title="Remove from driver"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Header */}
      <div className={styles.cardHeader}>
        <div className="flex items-center gap-1">
          <button
            className={`${styles.proLink}${move.pronumber === highlightPronumber ? ` ${styles.proLinkViewed}` : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              onProClick?.(move.pronumber, move.legnum);
            }}
          >
            {move.pronumber}-{move.legnum}
          </button>
          {onProDetailsClick && (
            <button
              className={styles.proDetailsButton}
              onClick={(e) => {
                e.stopPropagation();
                onProDetailsClick(move.pronumber);
              }}
              title="Open PRO details"
            >
              <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
            </button>
          )}
          {/* Trouble Ticket Badge */}
          {move.trouble_ticket_count != null && move.trouble_ticket_count > 0 && (() => {
            const tickets = move.trouble_tickets || [];
            const hasActive = tickets.some(
              (t) => t.status.toUpperCase() === 'NEW' || t.status.toUpperCase() === 'PENDING'
            );
            return (
              <Tooltip
                content={
                  <div className="text-xs space-y-2 max-w-xs">
                    <div className="font-semibold border-b border-gray-300 pb-1 mb-1 text-gray-900">
                      Driver Trouble Tickets ({move.trouble_ticket_count})
                    </div>
                    {tickets.map((ticket) => (
                      <div key={ticket.recno} className="space-y-0.5">
                        <div className="flex items-center gap-1.5">
                          <span className="text-gray-900 font-medium">{ticket.category || 'No Category'}</span>
                          <span className={`inline-flex px-1 py-0.5 rounded text-[0.625rem] font-medium ${
                            ticket.status.toUpperCase() === 'NEW' || ticket.status.toUpperCase() === 'PENDING'
                              ? 'bg-danger text-danger-fg'
                              : ticket.status.toUpperCase() === 'RESOLVED'
                                ? 'bg-ok text-ok-fg'
                                : 'bg-gray-100 text-gray-800'
                          }`}>
                            {ticket.status}
                          </span>
                        </div>
                        <div className="text-gray-400 text-[0.625rem]">{ticket.datetime}</div>
                      </div>
                    ))}
                  </div>
                }
                placement="bottom"
              >
                <span
                  className={`inline-flex items-center justify-center px-1 py-0.5 text-[0.5625rem] font-bold text-white rounded cursor-help whitespace-nowrap ${
                    hasActive ? 'bg-red-500' : 'bg-green-500'
                  }`}
                >
                  TT: {move.trouble_ticket_count}
                </span>
              </Tooltip>
            );
          })()}
          {/* Key Account / New Location Badge */}
          {showAccountStatus && (() => {
            // New Location takes priority
            if (move.dest_is_new_location === 'Y') {
              return (
                <span className="px-1 py-0.5 text-[0.5625rem] font-semibold rounded text-white bg-blue-500">
                  NEW
                </span>
              );
            }
            // Key Account badges
            const keyAccount = (move.key_account || '').toUpperCase();
            if (keyAccount === 'KEY ACCOUNTS 1') {
              return (
                <span className="px-1 py-0.5 text-[0.5625rem] font-semibold rounded text-white bg-red-500">
                  KEY 1
                </span>
              );
            }
            if (keyAccount === 'KEY ACCOUNTS 2') {
              return (
                <span className="px-1 py-0.5 text-[0.5625rem] font-semibold rounded text-white bg-orange-500">
                  KEY 2
                </span>
              );
            }
            if (keyAccount === 'KEY ACCOUNTS 3') {
              return (
                <span className="px-1 py-0.5 text-[0.5625rem] font-semibold rounded text-black bg-yellow-400">
                  KEY 3
                </span>
              );
            }
            if (keyAccount === 'KEY ACCOUNTS 4') {
              return (
                <span className="px-1 py-0.5 text-[0.5625rem] font-semibold rounded text-white bg-green-500">
                  KEY 4
                </span>
              );
            }
            if (keyAccount === 'CONTRACT') {
              return (
                <span className="px-1 py-0.5 text-[0.5625rem] font-semibold rounded text-black bg-lime-400">
                  CONTRACT
                </span>
              );
            }
            if (keyAccount === 'KEY PROSPECT 1') {
              return (
                <span className="px-1 py-0.5 text-[0.5625rem] font-semibold rounded text-white bg-purple-500">
                  KP1
                </span>
              );
            }
            if (keyAccount === 'KEY PROSPECT 2') {
              return (
                <span className="px-1 py-0.5 text-[0.5625rem] font-semibold rounded text-white bg-purple-500">
                  KP2
                </span>
              );
            }
            if (keyAccount === 'KEY PROSPECT 3') {
              return (
                <span className="px-1 py-0.5 text-[0.5625rem] font-semibold rounded text-white bg-purple-500">
                  KP3
                </span>
              );
            }
            if (keyAccount === 'KEY PROSPECT 4') {
              return (
                <span className="px-1 py-0.5 text-[0.5625rem] font-semibold rounded text-white bg-purple-500">
                  KP4
                </span>
              );
            }
            if (keyAccount === 'HYPER FOCUS') {
              return (
                <span className="px-1 py-0.5 text-[0.5625rem] font-semibold rounded text-white bg-blue-500">
                  HYPER
                </span>
              );
            }
            // Average or unknown - no badge
            return null;
          })()}
          <span className={`${styles.moveTypeBadge} ${move.movetype === 'IMPORT' ? styles.moveTypeBadgeImport : styles.moveTypeBadgeExport}`}>
            {move.movetype}
          </span>
          <span className={`${styles.liveDropBadge} ${move.legdrop === 'Y' ? styles.dropBadge : styles.liveBadge}`}>
            {move.legdrop === 'Y' ? 'DROP' : 'LIVE'}
          </span>
        </div>
        <div className="flex flex-col items-end gap-0.5">
          {(showContainerNumber && move.container) || (showSize && move.size) ? (
            <div className="flex items-center gap-1">
              {showContainerNumber && move.container && (
                <span className={styles.containerNumber}>{move.container}</span>
              )}
              {showSize && move.size && (
                <span className={styles.containerNumber}>{move.size}</span>
              )}
            </div>
          ) : null}
          {showChassis && move.chassisid && (
            <Tooltip content={`Chassis${move.chassistype ? ` (${move.chassistype})` : ''}: ${move.chassisid}`} placement="bottom">
              <span className={styles.containerNumber}>{move.chassisid}</span>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Route */}
      <div className={styles.cardRoute}>
        <span className={styles.originWithArrow}>
          <span title={move.origin_name}>{abbreviateLocationName(move.origin_name)}</span>
          <ArrowRightIcon className="w-3 h-3 flex-shrink-0 inline ml-1" />
        </span>
        <span title={move.dest_name}>{abbreviateLocationName(move.dest_name)}</span>
      </div>

      {/* City/State */}
      <div className={styles.cardLocations}>
        <span className="truncate">{move.origin_city_state}</span>
        <ArrowRightIcon className="w-2.5 h-2.5 flex-shrink-0" />
        <span className="truncate">{move.dest_city_state}</span>
        <span className="text-gray-400 ml-1">- ({formatMinutes(move.route_duration_minutes)})</span>
      </div>

      {/* Spacer */}
      <div className="flex-grow" />

      {/* Time Row */}
      <div className={styles.cardTimeRow}>
        {isCompleted ? (
          <>
            <div className={styles.timeLeftContainer}>
              <div className={styles.etaRow}>
                <span className={styles.etaLabel}>Arrive:</span>
                <span className={styles.etaTime}>{move.dest_arrival_time || '--'}</span>
              </div>
            </div>
            <div className={styles.deadlineContainer}>
              <div className={styles.etaRow}>
                <span className={styles.etaLabel}>Depart:</span>
                <span className={styles.etaTime}>{move.dest_depart_time || '--'}</span>
              </div>
            </div>
          </>
        ) : move.status === 'At Destination' ? (
          (() => {
            const hasAppt = (move.appointment_time && move.appointment_time.trim() !== '') || move.deadline_time?.startsWith('Appt:');
            const deadlineTimeMatch = move.deadline_time?.match(/(\d{1,2}):(\d{2})/);
            let deadlineMinutes = -1;
            let isAfter5PM = false;
            if (deadlineTimeMatch) {
              const hour = parseInt(deadlineTimeMatch[1], 10);
              const minute = parseInt(deadlineTimeMatch[2], 10);
              deadlineMinutes = hour * 60 + minute;
              isAfter5PM = hour >= 17;
            }
            const shouldShowLate = hasAppt || isAfter5PM;

            let arrivedLate = false;
            if (shouldShowLate && move.dest_arrival_time && deadlineMinutes >= 0) {
              const arrivalMatch = move.dest_arrival_time.match(/(\d{1,2}):(\d{2})/);
              if (arrivalMatch) {
                const arrHour = parseInt(arrivalMatch[1], 10);
                const arrMinute = parseInt(arrivalMatch[2], 10);
                const arrivalMinutes = arrHour * 60 + arrMinute;
                arrivedLate = arrivalMinutes > deadlineMinutes;
              }
            }

            return (
              <>
                <div className={styles.timeLeftContainer}>
                  <div className={styles.etaRow}>
                    <span className={styles.etaLabel}>Arrive:</span>
                    <span className={styles.etaTime}>{move.dest_arrival_time || '--'}</span>
                  </div>
                </div>
                <div className={styles.deadlineContainer}>
                  <span className={move.deadline_type === 'Per Diem' ? styles.deadlinePerDiem : styles.deadline}>
                    {displayedDeadlineTime}
                  </span>
                  {arrivedLate && (
                    <span className={styles.projectedStatusMissed}>Arrived Late</span>
                  )}
                  {exportSubDate && (
                    <span className={isDateToday(exportSubDate.date) ? styles.deadlinePerDiem : styles.deadlinePerDiemGrey}>
                      {exportSubDate.label} {exportSubDate.date}{exportSubDate.time ? ` ${exportSubDate.time}` : ''}
                    </span>
                  )}
                  {move.legtype === 'PICKUP' && (
                    move.storage_cleared_through ? (
                      <span className={isDateToday(move.storage_cleared_through) ? styles.deadlinePerDiem : styles.deadlinePerDiemGrey}>
                        Stor Clr: {move.storage_cleared_through}
                      </span>
                    ) : move.lfd ? (
                      <span className={isDateToday(move.lfd) ? styles.deadlinePerDiem : styles.deadlinePerDiemGrey}>
                        LFD: {move.lfd}
                      </span>
                    ) : null
                  )}
                </div>
              </>
            );
          })()
        ) : move.status === 'At Origin' && linkedArrivalTime ? (
          (() => {
            const hasAppt = (move.appointment_time && move.appointment_time.trim() !== '') || move.deadline_time?.startsWith('Appt:');
            const timeMatch = move.deadline_time?.match(/(\d{1,2}):(\d{2})/);
            let isAfter5PM = false;
            if (timeMatch) {
              const hour = parseInt(timeMatch[1], 10);
              isAfter5PM = hour >= 17;
            }
            const shouldShowStatus = hasAppt || isAfter5PM;

            return (
              <>
                <div className={styles.timeLeftContainer}>
                  <div className={styles.etaRow}>
                    <span className={styles.etaLabel}>ETA:</span>
                    <span className={`${styles.etaTime} ${
                      shouldShowStatus && (projectedStatus === 'missed' || projectedStatus === 'will_miss') ? styles.timeLeftNegative :
                      shouldShowStatus && projectedStatus === 'at_risk' ? styles.timeLeftAtRisk :
                      styles.timeLeftPositive
                    }`}>
                      {projectedETA || '--'}
                    </span>
                    <span className={styles.etaDestLabel}>to Dest</span>
                  </div>
                </div>
                <div className={styles.deadlineContainer}>
                  <span className={move.deadline_type === 'Per Diem' ? styles.deadlinePerDiem : styles.deadline}>
                    {displayedDeadlineTime}
                  </span>
                  {shouldShowStatus && projectedStatus === 'missed' && (
                    <span className={styles.projectedStatusMissed}>Late - Missed</span>
                  )}
                  {shouldShowStatus && projectedStatus === 'will_miss' && (
                    <span className={styles.projectedStatusMissed}>Late - Might Miss</span>
                  )}
                  {shouldShowStatus && projectedStatus === 'at_risk' && (
                    <span className={styles.projectedStatusAtRisk}>At Risk</span>
                  )}
                  {exportSubDate && (
                    <span className={isDateToday(exportSubDate.date) ? styles.deadlinePerDiem : styles.deadlinePerDiemGrey}>
                      {exportSubDate.label} {exportSubDate.date}{exportSubDate.time ? ` ${exportSubDate.time}` : ''}
                    </span>
                  )}
                  {move.legtype === 'PICKUP' && (
                    move.storage_cleared_through ? (
                      <span className={isDateToday(move.storage_cleared_through) ? styles.deadlinePerDiem : styles.deadlinePerDiemGrey}>
                        Stor Clr: {move.storage_cleared_through}
                      </span>
                    ) : move.lfd ? (
                      <span className={isDateToday(move.lfd) ? styles.deadlinePerDiem : styles.deadlinePerDiemGrey}>
                        LFD: {move.lfd}
                      </span>
                    ) : null
                  )}
                </div>
              </>
            );
          })()
        ) : isDueTomorrow ? (
          <>
            <div className={styles.timeLeftContainer}>
              <div className={styles.etaRow}>
                <span className={styles.apptTomorrowLabel}>Appt Tomorrow</span>
              </div>
            </div>
            <div className={styles.deadlineContainer}>
              <span className={move.deadline_type === 'Per Diem' ? styles.deadlinePerDiem : styles.deadline}>
                {move.deadline_date} {move.deadline_time}
              </span>
              {exportSubDate && (
                <span className={isDateToday(exportSubDate.date) ? styles.deadlinePerDiem : styles.deadlinePerDiemGrey}>
                  {exportSubDate.label} {exportSubDate.date}{exportSubDate.time ? ` ${exportSubDate.time}` : ''}
                </span>
              )}
              {move.legtype === 'PICKUP' && (
                move.storage_cleared_through ? (
                  <span className={isDateToday(move.storage_cleared_through) ? styles.deadlinePerDiem : styles.deadlinePerDiemGrey}>
                    Stor Clr: {move.storage_cleared_through}
                  </span>
                ) : move.lfd ? (
                  <span className={isDateToday(move.lfd) ? styles.deadlinePerDiem : styles.deadlinePerDiemGrey}>
                    LFD: {move.lfd}
                  </span>
                ) : null
              )}
            </div>
          </>
        ) : (
          (() => {
            const hasAppt = (move.appointment_time && move.appointment_time.trim() !== '') || move.deadline_time?.startsWith('Appt:');
            const timeMatch = move.deadline_time?.match(/(\d{1,2}):(\d{2})/);
            let isAfter5PM = false;
            if (timeMatch) {
              const hour = parseInt(timeMatch[1], 10);
              isAfter5PM = hour >= 17;
            }
            const shouldShowStatus = hasAppt || isAfter5PM;

            return (
              <>
                <div className={styles.timeLeftContainer}>
                  {move.eta_based_on_geofence && (
                    <div className={styles.geofenceLabel}>Based on Geofence</div>
                  )}
                  <div className={styles.etaRow}>
                    <span className={styles.etaLabel}>ETA:</span>
                    <span className={`${styles.etaTime} ${
                      shouldShowStatus && (projectedStatus === 'missed' || projectedStatus === 'will_miss') ? styles.timeLeftNegative :
                      shouldShowStatus && projectedStatus === 'at_risk' ? styles.timeLeftAtRisk :
                      styles.timeLeftPositive
                    }`}>
                      {projectedETA || '--'}
                    </span>
                    <span className={styles.etaDestLabel}>{isETAToDestination ? 'to Dest' : 'to Orig'}</span>
                  </div>
                  {/* Show destination ETA when showing origin ETA */}
                  {!isETAToDestination && projectedETAToDest && (
                    <div className={styles.etaRowSecondary}>
                      <span className={styles.etaDestLabel}>ETA:</span>
                      <span className={styles.etaTimeSecondary}>{projectedETAToDest}</span>
                      <span className={styles.etaDestLabel}>to Dest</span>
                    </div>
                  )}
                  {move.status === 'In Transit' && move.distance_to_dest_miles != null && isETAToDestination && (
                    <div className={styles.distanceRow}>
                      <span className={styles.distanceLabel}>{move.distance_to_dest_miles} mi remaining{move.driver_speed_mph ? ` at ${move.driver_speed_mph} mph` : ''}</span>
                    </div>
                  )}
                </div>
                <div className={styles.deadlineContainer}>
                  <span className={move.deadline_type === 'Per Diem' ? styles.deadlinePerDiem : styles.deadline}>
                    {displayedDeadlineTime}
                  </span>
                  {shouldShowStatus && projectedStatus === 'missed' && (
                    <span className={styles.projectedStatusMissed}>Late - Missed</span>
                  )}
                  {shouldShowStatus && projectedStatus === 'will_miss' && (
                    <span className={styles.projectedStatusMissed}>Late - Might Miss</span>
                  )}
                  {shouldShowStatus && projectedStatus === 'at_risk' && (
                    <span className={styles.projectedStatusAtRisk}>At Risk</span>
                  )}
                  {exportSubDate && (
                    <span className={isDateToday(exportSubDate.date) ? styles.deadlinePerDiem : styles.deadlinePerDiemGrey}>
                      {exportSubDate.label} {exportSubDate.date}{exportSubDate.time ? ` ${exportSubDate.time}` : ''}
                    </span>
                  )}
                  {move.legtype === 'PICKUP' && (
                    move.storage_cleared_through ? (
                      <span className={isDateToday(move.storage_cleared_through) ? styles.deadlinePerDiem : styles.deadlinePerDiemGrey}>
                        Stor Clr: {move.storage_cleared_through}
                      </span>
                    ) : move.lfd ? (
                      <span className={isDateToday(move.lfd) ? styles.deadlinePerDiem : styles.deadlinePerDiemGrey}>
                        LFD: {move.lfd}
                      </span>
                    ) : null
                  )}
                </div>
              </>
            );
          })()
        )}
      </div>

      {/* Quick Assign button */}
      {!isCompleted && <RouteEta moveStatus={move.status} data={move.route_eta} cascade={routeCascade} />}
      {canRemove && onQuickAssign && (
        <div className={styles.quickAssignContainer} ref={quickAssignRef}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowQuickAssign(!showQuickAssign);
            }}
            className={styles.quickAssignButton}
            title="Quick assign to driver"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current">
              <path d="M17.244,4A2,2,0,0,0,13.83,5.414V7H9a9.01,9.01,0,0,0-9,9v7a1,1,0,0,0,2,0,6.006,6.006,0,0,1,6-6h5.83v1.586A2,2,0,0,0,17.244,20l5.879-5.879a3,3,0,0,0,0-4.242Z"/>
            </svg>
          </button>

          {showQuickAssign && (
            <div className={styles.quickAssignPopup}>
              <label className={styles.quickAssignLabel}>
                Unit Number
              </label>
              <input
                ref={quickAssignInputRef}
                type="text"
                value={quickAssignInput}
                onChange={(e) => setQuickAssignInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleQuickAssignSubmit();
                  } else if (e.key === 'Escape') {
                    setShowQuickAssign(false);
                    setQuickAssignInput('');
                  }
                }}
                placeholder="e.g., 1234"
                className={styles.quickAssignInput}
              />
              <button
                onClick={handleQuickAssignSubmit}
                disabled={!quickAssignInput.trim()}
                className={styles.quickAssignSubmitButton}
              >
                Assign
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MoveCard;
